/**
 * The Table plot type: statistics in rows and columns, as real HTML rather than
 * a canvas, so it can be sorted, selected, copied and scrolled.
 *
 * `tableTemplate` is shared by Explore and dashboard tiles; `renderTable` /
 * `destroyTable` are Explore's wrappers around `#chart-root`, the way the map
 * has its own. The pure helpers (columns, sorting, formatting, the sheet) are
 * exported for tests and for the Excel download.
 */
import { html, nothing, render, svg, type TemplateResult } from 'lit';
import type { TableResponse, TableRow } from '../../../api/types.ts';
import {
  plotState,
  setTablePage,
  setTablePageSize,
  setTableSort,
  type PlotState,
} from '../../../state/plot-state.ts';
import { shipLabel } from '../../../utils/ships.ts';
import { downloadXlsx, type XlsxCell } from '../../../utils/xlsx.ts';
import { runtime } from '../plot-runtime.ts';
import { chartReady, colorLegend } from '../plot-ui-state.ts';
import {
  TABLE_PAGE_SIZES,
  groupLabel,
  measureLabel,
  measureUnit,
  type TableMeasureState,
} from '../table-config.ts';
import '../../../ui/plain-select.ts';

/* ── Pure helpers ───────────────────────────────────────── */

export interface TableColumn {
  /** Sort id: `ship`, `k0`, `k1`, `n`, `m<i>`. */
  id: string;
  label: string;
  kind: 'key' | 'value';
  /** Index into a row's `values` for value columns. */
  measure?: number;
}

type Cell = number | string | boolean | null | undefined;

/**
 * The columns shown, left to right. A summary: ship, the group keys, the
 * measures, rows. Readings: ship, the time, then the columns in the order the
 * picker has them now - looked up by name, so reordering or removing a column
 * redraws what is loaded without asking again.
 */
export function tableColumns(
  data: TableResponse,
  measures: TableMeasureState[],
  columns: string[] = data.columns ?? [],
): TableColumn[] {
  const cols: TableColumn[] = [];
  if (data.rows.some((r) => r.ship)) cols.push({ id: 'ship', label: 'Ship', kind: 'key' });
  if (data.mode === 'readings') {
    cols.push({ id: 'k0', label: 'Time (UTC)', kind: 'key' });
    columns.forEach((name, i) => {
      const at = data.columns.indexOf(name);
      if (at >= 0) cols.push({ id: `m${i}`, label: name, kind: 'value', measure: at });
    });
    return cols;
  }
  data.groups.forEach((g, i) => {
    cols.push({
      id: `k${i}`,
      label: groupLabel(g.bucket ? { kind: g.kind, bucket: g.bucket, column: g.column } : { kind: g.kind, column: g.column }),
      kind: 'key',
    });
  });
  data.measures.forEach((m, i) => {
    // The label and decimals are display state and may have changed since the
    // fetch; the server's echo is the fallback for a tile with no state.
    const shown = { ...m, ...(measures[i] ?? {}) };
    const unit = measureUnit(m);
    cols.push({
      id: `m${i}`,
      label: `${measureLabel(shown)}${unit ? ` (${unit})` : ''}`,
      kind: 'value',
      measure: i,
    });
  });
  cols.push({ id: 'n', label: 'Rows', kind: 'value' });
  return cols;
}

/** A group key as it reads: a day, a band, a value. */
export function keyText(group: TableResponse['groups'][number], key: TableRow['keys'][number] | undefined): string {
  if (key === null || key === undefined) return '(no value)';
  if (group.kind === 'time' && typeof key === 'string') {
    const iso = key.replace('T', ' ');
    if (!group.bucket) return iso.slice(0, 19);
    if (group.bucket === 'hour') return iso.slice(0, 16);
    if (group.bucket === 'month') return iso.slice(0, 7);
    if (group.bucket === 'week') return `Week of ${iso.slice(0, 10)}`;
    return iso.slice(0, 10);
  }
  if (group.kind === 'bands' && typeof key === 'number') {
    const e = group.edges;
    const f = (v: number): string => formatValue(v, null);
    if (!e.length) return 'All';
    if (key <= 0) return `< ${f(e[0]!)}`;
    if (key >= e.length) return `≥ ${f(e[e.length - 1]!)}`;
    return `${f(e[key - 1]!)} – ${f(e[key]!)}`;
  }
  return String(key);
}

/**
 * A value as text. With `decimals` null the places follow the size of the
 * number, so 12,345 and 0.0123 both read without a column of trailing zeros.
 */
export function formatValue(v: number | null | undefined, decimals: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  let dp = decimals;
  if (dp == null) {
    const a = Math.abs(v);
    dp = a === 0 || Number.isInteger(v) ? 0 : a >= 1000 ? 0 : a >= 100 ? 1 : a >= 1 ? 2 : 3;
  }
  dp = Math.max(0, Math.min(10, Math.round(dp)));
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(
    Object.is(v, -0) ? 0 : v,
  );
}

/** Any cell: numbers formatted, text as it is. */
export function formatCell(v: Cell, decimals: number | null | undefined): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return formatValue(v, decimals);
}

/**
 * Decimal places for a whole column, so its figures line up: from the largest
 * value in it, the way `formatValue` would pick for that one number. A column
 * of whole numbers (counts, rows) gets none.
 */
export function columnDecimals(values: Cell[]): number {
  const finite = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!finite.length || finite.every((v) => Number.isInteger(v))) return 0;
  const a = Math.max(...finite.map((v) => Math.abs(v)));
  return a >= 1000 ? 0 : a >= 100 ? 1 : a >= 1 ? 2 : 3;
}

/** The value behind a cell, for sorting: numbers as numbers, keys in load order. */
function sortValue(row: TableRow, id: string, index: number, at?: number): number | string | null {
  if (id === 'ship') return shipLabel(row.ship ?? '');
  if (id === 'n') return row.n;
  if (id.startsWith('k')) return index; // rows arrive in key order already
  const v = row.values[at ?? Number(id.slice(1))];
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  return String(v);
}

/**
 * Rows in the chosen order. Empty values sink to the bottom either way.
 * Readings arrive already sorted by the server, over every reading in range.
 */
export function shownRows(data: TableResponse, sort: string, cols: TableColumn[]): TableRow[] {
  return data.mode === 'readings' ? data.rows : sortRows(data.rows, sort, cols);
}

/** Rows in the chosen order. Empty values sink to the bottom either way. */
export function sortRows(rows: TableRow[], sort: string, cols?: TableColumn[]): TableRow[] {
  const [id, dir] = sort.split(':');
  if (!id || (dir !== 'asc' && dir !== 'desc')) return rows;
  const sign = dir === 'asc' ? 1 : -1;
  const at = cols?.find((c) => c.id === id)?.measure;
  const indexed = rows.map((row, i) => ({ row, i, v: sortValue(row, id, i, at) }));
  indexed.sort((a, b) => {
    if (a.v === null && b.v === null) return a.i - b.i;
    if (a.v === null) return 1;
    if (b.v === null) return -1;
    const c = typeof a.v === 'string' || typeof b.v === 'string'
      ? String(a.v).localeCompare(String(b.v))
      : a.v - b.v;
    return c * sign || a.i - b.i;
  });
  return indexed.map((x) => x.row);
}

/** The next sort after clicking a header: ascending, then descending, then off. */
export function nextSort(current: string, id: string): string {
  const [cid, dir] = current.split(':');
  if (cid !== id) return `${id}:asc`;
  if (dir === 'asc') return `${id}:desc`;
  return '';
}

interface TotalLine {
  label: string;
  n: number;
  values: Cell[];
}

function totalLines(data: TableResponse): TotalLine[] {
  if (data.totals?.length) {
    return data.totals.map((t) => ({ label: `Total ${shipLabel(t.ship)}`, n: t.n, values: t.values }));
  }
  return data.total ? [{ label: 'Total', n: data.total.n, values: data.total.values }] : [];
}

/** The table as a sheet: what is on screen, in its sort, numbers as numbers. */
export function tableSheetRows(data: TableResponse, s: PlotState): XlsxCell[][] {
  const cols = tableColumns(data, s.table_measures, readingColumns(data, s));
  const rows = shownRows(data, s.table_sort, cols);
  const cell = (row: TableRow, c: TableColumn): XlsxCell => {
    if (c.id === 'ship') return shipLabel(row.ship ?? '');
    if (c.id === 'n') return row.n;
    if (c.kind === 'key') {
      const i = Number(c.id.slice(1));
      return keyText(data.groups[i]!, row.keys[i]);
    }
    const v = row.values[c.measure!];
    return typeof v === 'boolean' ? String(v) : (v ?? null);
  };
  const out: XlsxCell[][] = [cols.map((c) => c.label), ...rows.map((r) => cols.map((c) => cell(r, c)))];
  for (const t of totalLines(data)) {
    out.push(
      cols.map((c, i) => {
        if (c.id === 'n') return t.n;
        if (c.kind === 'value') {
          const v = t.values[c.measure!];
          return typeof v === 'boolean' ? String(v) : (v ?? null);
        }
        return i === 0 ? t.label : null;
      }),
    );
  }
  return out;
}

/**
 * The readings columns to show: the picker's, when this state is the one that
 * fetched them; the response's own on a tile or in a test with no picker.
 */
function readingColumns(data: TableResponse, s: PlotState): string[] {
  if (data.mode !== 'readings') return [];
  const picked = s.table_mode === 'readings' ? s.table_columns.filter((c) => data.columns.includes(c)) : [];
  return picked.length ? picked : data.columns;
}

/* ── Template ───────────────────────────────────────────── */

const DOWNLOAD_GLYPH = svg`<svg class="chart-action-glyph btn-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

export interface TableTemplateOpts {
  /** Header clicks sort; without it the headers are plain. */
  onSort?: (sort: string) => void;
  /** Show the Excel button. */
  download?: boolean;
  /** Font size in px, from the chart text size setting. */
  fontSize?: number;
  /** Readings: turn the page, or change its size. Without them, no pager. */
  onPage?: (page: number) => void;
  onPageSize?: (size: number) => void;
}

/** Where a readings page sits: "1,001–1,500 of 14,641", page 3 of 30. */
export function pageInfo(data: TableResponse): { from: number; to: number; total: number; page: number; pages: number } {
  const limit = data.limit || data.rows.length || 1;
  const offset = data.offset ?? 0;
  const total = data.max_ship_rows ?? data.source_rows;
  const shipRows = data.rows.some((r) => r.ship)
    ? Math.max(0, ...Object.values(
        data.rows.reduce<Record<string, number>>((m, r) => ({ ...m, [r.ship!]: (m[r.ship!] ?? 0) + 1 }), {}),
      ))
    : data.rows.length;
  return {
    from: shipRows ? offset + 1 : 0,
    to: offset + shipRows,
    total,
    page: Math.floor(offset / limit),
    pages: Math.max(1, Math.ceil(total / limit)),
  };
}

function pager(data: TableResponse, opts: TableTemplateOpts): TemplateResult | typeof nothing {
  if (data.mode !== 'readings' || !opts.onPage) return nothing;
  const p = pageInfo(data);
  const n = (v: number): string => v.toLocaleString('en-US');
  const go = (page: number) => () => opts.onPage!(page);
  return html`<span class="data-table-pager">
    <button class="chart-icon-btn" title="First page" ?disabled=${p.page <= 0} @click=${go(0)}>«</button>
    <button class="chart-icon-btn" title="Previous page" ?disabled=${p.page <= 0} @click=${go(p.page - 1)}>‹</button>
    <span class="data-table-page">Page ${n(p.page + 1)} of ${n(p.pages)}</span>
    <button class="chart-icon-btn" title="Next page" ?disabled=${p.page >= p.pages - 1} @click=${go(p.page + 1)}>›</button>
    <button class="chart-icon-btn" title="Last page" ?disabled=${p.page >= p.pages - 1} @click=${go(p.pages - 1)}>»</button>
    ${opts.onPageSize
      ? html`<plain-select
          class="data-table-size"
          label="Rows per page"
          alignRight
          .value=${String(data.limit ?? 500)}
          .options=${TABLE_PAGE_SIZES.map((v) => ({ value: String(v), label: `${n(v)} / page` }))}
          .select=${(v: string) => opts.onPageSize!(Number(v))}
        ></plain-select>`
      : nothing}
  </span>`;
}

/** The count on the left of the bar: the page's span for readings, else rows. */
function countText(data: TableResponse, shown: number): TemplateResult {
  const n = (v: number): string => v.toLocaleString('en-US');
  if (data.mode === 'readings') {
    const p = pageInfo(data);
    return html`${n(p.from)}–${n(p.to)} of ${n(p.total)} readings`;
  }
  return html`${n(shown)} ${shown === 1 ? 'row' : 'rows'}${
    data.rows_left_out ? html` <span class="data-table-more">· ${n(data.rows_left_out)} more not shown</span>` : nothing
  }`;
}

export function tableTemplate(data: TableResponse, s: PlotState, opts: TableTemplateOpts = {}): TemplateResult {
  const cols = tableColumns(data, s.table_measures, readingColumns(data, s));
  const rows = shownRows(data, s.table_sort, cols);
  const [sortId, sortDir] = s.table_sort.split(':');
  const totals = totalLines(data);
  // Auto decimals are settled per column, over the rows and totals together.
  const autoDp = new Map<number, number>();
  const width = data.mode === 'readings' ? data.columns.length : data.measures.length;
  for (let i = 0; i < width; i++) {
    autoDp.set(i, columnDecimals([...data.rows.map((r) => r.values[i]), ...totals.map((t) => t.values[i])]));
  }
  const chosen = (i: number): number | null | undefined =>
    data.mode === 'readings' ? null : s.table_measures[i]?.decimals;
  const decimals = (c: TableColumn): number =>
    c.measure != null ? (chosen(c.measure) ?? autoDp.get(c.measure) ?? 2) : 0;
  const header = (c: TableColumn, i: number): TemplateResult => {
    const sorted = sortId === c.id;
    const cls = `${c.kind === 'key' ? 'data-table-key' : ''} ${i === 0 ? 'data-table-first' : ''} ${sorted ? 'sorted' : ''}`;
    const aria = sorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
    const caret = sorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    if (!opts.onSort) return html`<th scope="col" class=${cls}>${c.label}</th>`;
    return html`<th scope="col" class="${cls} data-table-sort" aria-sort=${aria}
      title="Sort by ${c.label}" @click=${() => opts.onSort!(nextSort(s.table_sort, c.id))}>${c.label}${caret}</th>`;
  };
  const cell = (row: TableRow, c: TableColumn, i: number): TemplateResult => {
    const cls = `${c.kind === 'key' ? 'data-table-key' : ''} ${i === 0 ? 'data-table-first' : ''} ${sortId === c.id ? 'sorted' : ''}`;
    if (c.id === 'ship') return html`<th scope="row" class=${cls}>${shipLabel(row.ship ?? '')}</th>`;
    if (c.kind === 'key') {
      const k = Number(c.id.slice(1));
      const text = keyText(data.groups[k]!, row.keys[k]);
      return i === 0 ? html`<th scope="row" class=${cls}>${text}</th>` : html`<td class=${cls}>${text}</td>`;
    }
    const v = c.id === 'n' ? row.n : row.values[c.measure!];
    // Text reads from the left, like a key; numbers line up on the right.
    const text = typeof v === 'string' ? ' data-table-key' : '';
    return html`<td class="${cls}${text}">${formatCell(v, decimals(c))}</td>`;
  };
  const style = opts.fontSize ? `font-size:${opts.fontSize}px` : '';
  return html`<div class="data-table-host">
    ${opts.download
      ? html`<div class="data-table-bar">
          <span class="data-table-count">${countText(data, rows.length)}</span>
          ${pager(data, opts)}
          <button class="chart-icon-btn" title=${data.mode === 'readings'
              ? 'Download this page as an Excel workbook - the Export tab has every reading'
              : 'Download this table as an Excel workbook'}
            @click=${() => downloadTable(data, s)}>${DOWNLOAD_GLYPH} Excel</button>
        </div>`
      : nothing}
    <div class="data-table-scroll scroll-thin">
      <table class="data-table" style=${style}>
        <thead><tr>${cols.map(header)}</tr></thead>
        <tbody>${rows.map((r) => html`<tr>${cols.map((c, i) => cell(r, c, i))}</tr>`)}</tbody>
        ${totals.length
          ? html`<tfoot>${totals.map(
              (t) => html`<tr>${cols.map((c, i) => {
                if (c.id === 'n') return html`<td>${formatValue(t.n, 0)}</td>`;
                if (c.kind === 'value') return html`<td>${formatCell(t.values[c.measure!], decimals(c))}</td>`;
                return i === 0
                  ? html`<th scope="row" class="data-table-key data-table-first">${t.label}</th>`
                  : html`<td class="data-table-key"></td>`;
              })}</tr>`,
            )}</tfoot>`
          : nothing}
      </table>
    </div>
  </div>`;
}

function downloadTable(data: TableResponse, s: PlotState): void {
  const day = new Date().toISOString().slice(0, 10);
  downloadXlsx(`table_${day}.xlsx`, [{ name: 'Table', rows: tableSheetRows(data, s) }]);
}

/* ── Explore ────────────────────────────────────────────── */

let exploreHost: HTMLElement | null = null;

export function renderTable(data: TableResponse): void {
  const root = runtime.rootEl;
  if (!root) return;
  if (runtime.instance) {
    runtime.instance.destroy();
    runtime.instance = null;
  }
  if (!exploreHost || exploreHost.parentElement !== root) {
    root.innerHTML = '';
    exploreHost = document.createElement('div');
    exploreHost.className = 'data-table-mount';
    root.appendChild(exploreHost);
  }
  const s = plotState.value;
  render(
    tableTemplate(data, s, {
      onSort: setTableSort,
      download: true,
      fontSize: s.style.font_size,
      onPage: setTablePage,
      onPageSize: (size) => setTablePageSize(size, true),
    }),
    exploreHost,
  );
  // The table is HTML, not a canvas: the image export has nothing to capture.
  chartReady.set(false);
  colorLegend.set(null);
}

/** Take the table out of `#chart-root` (switching to a canvas type). */
export function destroyTable(): void {
  if (!exploreHost) return;
  render(nothing, exploreHost);
  exploreHost.remove();
  exploreHost = null;
}
